import React, { Ref, forwardRef, useCallback } from 'react';
import { ValidationOptions, ErrorMessage, FieldErrors } from 'react-hook-form';
import styled from 'styled-components/macro';

interface ITextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label: string;
  value?: string;
  disabled?: boolean;
  validation?: ValidationOptions;
  errors?: FieldErrors<Record<string, unknown>>;
  autoFocus?: boolean;
  register?: (ref: Element, validationOptions?: ValidationOptions) => Ref<HTMLInputElement>;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const TextInput = forwardRef<HTMLInputElement, ITextInputProps>(
  (
    {
      name,
      label,
      value,
      disabled,
      validation,
      required,
      errors,
      autoFocus,
      className,
      register,
      onChange,
      onBlur,
      ...rest
    }: ITextInputProps,
    ref,
  ): JSX.Element | null => {
    if (!(register || (onChange && value !== undefined))) {
      throw new Error('Please provide one of `register` (from react-hook-form) or `value` and `onChange`');
    }
    if ((validation && !errors) || (!validation && errors)) {
      throw new Error('Please provide either both or none of `validation` and `errors`');
    }
    const isValid = !(errors && errors[name]);

    const refCallback = useCallback(
      (element: HTMLInputElement | null): void => {
        if (!element) return;
        if (register) {
          register(element, validation);
        }
        if (typeof ref === 'function') {
          ref(element);
        } else if (ref?.current) {
          ref.current = element;
        }
      },
      [ref, validation, register],
    );

    return (
      <Label className={className}>
        {label && <LabelText required={!!validation?.required || !!required}>{label}</LabelText>}
        <Input
          name={name}
          ref={refCallback}
          value={value}
          className={isValid ? '' : 'invalid'}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
          aria-label={name}
          onChange={onChange}
          onBlur={onBlur}
          {...rest}
        />
        {!isValid && (
          <ErrorMessageContainer>
            <ErrorMessage name={name} errors={errors} />
          </ErrorMessageContainer>
        )}
      </Label>
    );
  },
);

TextInput.displayName = 'TextInput';

const Label = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 14px;
`;

interface ILabelTextProps {
  required?: boolean;
}

export const LabelText = styled.div<ILabelTextProps>`
  margin-bottom: 10px;

  font-size: 14px;
  font-weight: 600;

  &[required]:after {
    content: '*';
    color: var(--chakra-colors-text-danger);
    margin-left: 2px;
  }
`;

const Input = styled.input`
  height: 38px;
  width: 100%;
  border-radius: 7px;
  border: 1px solid hsl(0, 0%, 82%);
  padding: 9px 12px;
  font-size: 14px;
  font-family: inherit;
  background-color: var(--chakra-colors-background-default);
  color: hsl(235, 81%, 13%);
  transition: border-color 0.1s ease-in-out;

  &:hover:not([disabled]) {
    border-color: hsl(0, 0%, 70%);
  }

  &.invalid:not([disabled]) {
    border-color: #da4167;
  }

  &:focus:not([disabled]) {
    border-color: var(--chakra-colors-text-brand);
  }

  &[disabled] {
    background: #f3f3f3;
    opacity: 0.8;
  }
`;

const ErrorMessageContainer = styled.div`
  margin-top: 8px;
  color: var(--chakra-colors-text-danger);
  line-height: 1.5;
`;

export default TextInput;
